/** Detail view for a clicked secondary-conductor line (Phase 3 step 14 / Phase 1
 *  note that DetailPanel needs a `secondary` case).
 *
 *  Renders the imported segment attributes plus, when the color-by-confidence
 *  mode supplied them (`include_connectivity`), the derived per-component rooting
 *  (transformer + method/confidence/tier). Everything is read straight from the
 *  clicked GeoJSON feature properties — no extra fetch — and degrades when the
 *  derived keys are absent (plain mode) or null (no published run). */

import type { ConnectivityTier, RootMethod } from '../../types/secondary.ts'
import { TierBadge } from './SecondaryConnectionSection.tsx'

interface SecondaryDetailProps {
  feature: Record<string, unknown>
  onNavigate: (type: string, id: number) => void
}

const ROOT_METHOD_LABELS: Record<RootMethod, string> = {
  manual_override: 'Manual override',
  meter_majority_strong: 'Meter majority (strong)',
  meter_majority_weak: 'Meter majority (weak)',
  node_id: 'Node ID',
  geom_snap: 'Geometry snap',
  unrooted: 'Unrooted',
}

export function SecondaryDetail({ feature, onNavigate }: SecondaryDetailProps) {
  const gisId = feature.gis_id as string | null | undefined
  const id = feature.id as number | undefined
  const segmentType = feature.segment_type as string | null | undefined
  const phase = feature.phase as string | null | undefined
  const conductorSize = feature.conductor_size as string | null | undefined
  const voltage = feature.voltage as number | string | null | undefined
  const circuitName = feature.circuit_name as string | null | undefined

  // Derived rooting props are present only when include_connectivity was on; a
  // missing key (plain mode) and a null value (no published run) both read as
  // "not computed".
  const hasConnectivityKeys = 'tier' in feature || 'root_method' in feature
  const rootTransformerId = feature.root_transformer_id as number | null | undefined
  const rootMethod = feature.root_method as RootMethod | null | undefined
  const rootConfidence = feature.root_confidence as number | null | undefined
  const tier = feature.tier as ConnectivityTier | null | undefined
  const transformerReachable = feature.transformer_reachable as boolean | null | undefined

  const title = gisId ?? (id != null ? `Secondary #${id}` : 'Secondary')

  return (
    <div className="space-y-3">
      {/* Header */}
      <div>
        <div className="text-base font-semibold text-gray-900 truncate" title={title}>
          {title}
        </div>
        {circuitName && <div className="text-sm text-gray-500">{circuitName}</div>}
      </div>

      {/* Imported segment attributes */}
      <div className="space-y-1.5 text-sm">
        <DetailRow label="Type" value={segmentType} />
        <DetailRow label="Phase" value={phase} />
        <DetailRow label="Conductor Size" value={conductorSize} />
        <DetailRow label="Voltage" value={voltage} />
      </div>

      {/* Derived rooting (color-by-confidence) */}
      <div className="border-t border-gray-200 pt-3">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
          Derived Rooting
        </p>
        {!hasConnectivityKeys || !tier ? (
          <div className="text-sm text-gray-400">
            Not computed
            {!hasConnectivityKeys && (
              <span className="block text-xs text-gray-400 mt-0.5">
                Enable color-by-confidence to load derived props
              </span>
            )}
          </div>
        ) : (
          <div className="space-y-2">
            {rootTransformerId != null ? (
              <button
                onClick={() => onNavigate('Transformer', rootTransformerId)}
                className="w-full text-left p-2 rounded bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-violet-400" />
                  <span className="text-sm font-medium text-gray-700">
                    Transformer #{rootTransformerId}
                  </span>
                  <TierBadge tier={tier} />
                </div>
              </button>
            ) : (
              <div className="p-2 rounded bg-gray-50 text-sm text-gray-500 flex items-center gap-2">
                <span>No rooted transformer</span>
                <TierBadge tier={tier} />
              </div>
            )}
            <div className="space-y-1 text-sm">
              <DetailRow
                label="Method"
                value={rootMethod ? ROOT_METHOD_LABELS[rootMethod] ?? rootMethod : null}
              />
              <DetailRow label="Confidence" value={formatConfidence(rootConfidence)} />
              <ReachableRow reachable={transformerReachable} />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function ReachableRow({ reachable }: { reachable: boolean | null | undefined }) {
  if (reachable === null || reachable === undefined) return null
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">Transformer Reachable</span>
      <span className={`font-medium ${reachable ? 'text-green-600' : 'text-amber-600'}`}>
        {reachable ? 'Yes' : 'No'}
      </span>
    </div>
  )
}

function formatConfidence(confidence: number | null | undefined): string | null {
  if (confidence === null || confidence === undefined) return null
  return `${(confidence * 100).toFixed(0)}%`
}

function DetailRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  if (value === null || value === undefined || value === '') return null
  return (
    <div className="flex justify-between">
      <span className="text-gray-500">{label}</span>
      <span className="text-gray-800 font-medium">{String(value)}</span>
    </div>
  )
}
